import { cart } from "@/theme/Cart";
import { CartMain } from "../Cart/CartMain";
import { Component, Prop, Ref, Watch } from "@eastsideco/salvo-ts";
import { debounce } from 'lodash';
import { ProductOptionPicker } from "./ProductOptionPicker";
import { QuantitySpinner } from "../QuantitySpinner/QuantitySpinner";
import { setVariantUrlParam, getQuantityMax, formatPrice} from "@/utils/products";
import { SideCart } from '../SideCart/SideCart';
import { ThemeComponent } from "@/theme/ThemeComponent";

/**
 * A form which allows customers to select product options and then
 * add the matching variant and quantity to the cart.
 */
@Component
export class ProductForm extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop optionStyle!: 'dropdown'|'buttons';
    @Prop variantsQty!: string;
    @Prop strings!: string;
    @Ref({required:false}) options!: ProductOptionPicker;
    @Ref submitButton!: HTMLButtonElement;
    @Ref error!: HTMLElement;
    @Ref({required:false}) quantity!: QuantitySpinner|null;

    @Ref({required: false}) tests!: HTMLElement[];

    protected loading: boolean = false;
    protected selectedVariant: Shopify.Liquid.Variant|null = null;
    protected quantityVal: number = 1;
    protected maxQty: string = '1';
    protected selectedCartQty: number = 0;
    private cartMain:CartMain = document.body.querySelector('cart-main') as CartMain;
    private sideCart:SideCart|null = document.body.querySelector('side-cart');

    init() {
        this.$subscribe(this.options.onSelectedVariantChange, (variant) => this.selectedVariant = variant);
        this.selectedVariant = this.options.selectedVariant;

        // Quantity Funtions
        this.setQuantityMax();
        this.$subscribe(this.cartMain.onCartItemCountChange, debounce(() => this.setQuantityMax(), 300));



        this.$listen(this.submitButton, 'click', () => {
            this.addToCart();
        });
    }

    protected showError(msg: string|null): void {
        this.error.textContent = msg;
        this.error.classList.add('is-active');
        this.error.style.display = msg ? '' : 'none';
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {
        setVariantUrlParam(this.selectedVariant?.id || null);
        this.setQuantityMax();
        this.handlePrice();
    }

    @Watch('loading')
    protected handleLoading() {
        this.submitButton.disabled = this.loading;
    }

    /**
     *  Update Price;
     * @returns
     */
    handlePrice() {
        if (this.selectedVariant ===  null) { return; }
        const moneyFormat = this.getString('moneyFormat');
        const updatedPrice = formatPrice(this.selectedVariant.price, moneyFormat);

        if (updatedPrice === null) { return; }
        const priceElement = document.querySelector('product-price__money');
        if(!priceElement) {
            return;
        }
        priceElement.innerHTML = updatedPrice;
    }

    /**
     * Set maximum variant quantity on variant change.
     */
    protected setQuantityMax() {
        this.maxQty = getQuantityMax(this.selectedVariant?.id || null, this.variantsQty);
        this.selectedCartQty = this.checkCartQty(this.selectedVariant);

        if (this.selectedCartQty > 0) {
            this.maxQty = String(~~this.maxQty - this.selectedCartQty);
        }

        this.handleQtyInput();
        this.handleQtyButtons();

        if (~~this.maxQty <= 0) {
            this.handleOutOfStock();
            this.quantity?.input.setAttribute('max', this.maxQty);
            this.quantity?.classList.add('visually-hidden');
            return;
        }

        this.quantity?.input.setAttribute('max', this.maxQty);
        this.quantity?.classList.remove('visually-hidden');
        this.handleInStock();
    }

    handleQtyInput() {
        const qtyInput = this.quantity?.input.value;

        if (qtyInput && (~~qtyInput > ~~this.maxQty)) {
            if (!this.quantity) { return; }
            this.quantity.input.value = this.maxQty;
            return;
        }
    }

    handleQtyButtons() {
        if (!this.quantity) { return; }

        if (~~this.quantity.input.value >= ~~this.maxQty) {
            this.quantity.increaseButton.disabled = true;
            return;
        }

        this.quantity.increaseButton.disabled = false;
    }

    /**
     * Check if selected variant is in cart? get quanity.
     * @param variant - selected variant.
     * @returns
     */
    protected checkCartQty(variant: Shopify.Liquid.Variant|null) {
        if (variant === null || cart.items.length === 0) { return 0; }

        let quantity = 0;
        for (const item of cart.items) {
            if (item.id === variant.id) {
                quantity = item.quantity;
            }
        }

        return quantity;
    }

    /**
     * If quantity switcher exists, update Quantity on value change.
     */


    /**
     * Handle Add to Cart.
     */
    protected addToCart() {
        this.loading = true;
        const variant = this.selectedVariant;
        if (!variant) {
            this.showError(`Please select an option.`);
            return;
        }

        const itemInCart:boolean = this.cartMain.checkCart(variant.id);
        cart.addItem({
            id: variant.id,
            quantity: this.quantityVal,
        }).then((item) => {
            if (itemInCart) {
                this.cartMain.updateCart('update', item, item.quantity);

                if (!this.sideCart) { return; }
                this.sideCart.openCart();
            } else {
                this.cartMain.updateCart('add', item);
                document.dispatchEvent(new Event('cartAdded'));
            }
        }).catch((event: unknown | null) => {
            if (event instanceof Error) {
                this.showError(event.message);
            }
        });

        this.loading = false;
    }

    /**
     * Diasable AdToCart if variant is out of stock.
     */
    protected handleOutOfStock() {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('button--disabled');
        this.submitButton.innerHTML = this.getString('soldOut');
    }

    /**
     * Enable AddToCart.
     */
    protected handleInStock() {
        if (this.selectedVariant ===  null) { return; }
        const moneyFormat = this.getString('moneyFormat');
        const updatedPrice = `- ${formatPrice(this.selectedVariant.price, moneyFormat)}`;
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('button--disabled');
        this.submitButton.innerHTML = `${this.getString('add')} ${updatedPrice}`;
    }

    /**
     * Get Language string set within the product template.
     * @param string
     * @returns
     */
    protected getString(string: string) {
        const strings = JSON.parse(this.strings);
        return strings[string];
    }
}
